import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';
import { createEagerSelector, eagerMetaFactory } from '../common/reducers/pendingUpdateSlice';

const compareAssistance = (a1, a2) => a1.internalid === a2.internalid;

const jobAssistanceSlice = createSlice({
    name: 'jobAssistance',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            let data = action.payload;
            if (!Array.isArray(data)) {
                data = [data];
            }
            data.forEach(dt => {
                delete state.data[dt.jobid?.toString()];
            })
            data.forEach(dt => {
                const jobid = dt.jobid?.toString();
                if (!state.data[jobid]) {
                    state.data[jobid] = []
                }
                state.data[jobid].push(dt);
            });
        },
        addOrUpdate: (state, action) => {
            for (const assist of ensureArray(action.payload)) {
                const jobid = assist.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareAssistance(a, assist));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1, assist);
                    } else {
                        state.data[jobid].push(assist);
                    }
                } else {
                    state.data[jobid] = [assist];
                }
            }
        },
        remove: (state, action) => {
            for (const assist of ensureArray(action.payload)) {
                const jobid = assist.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareAssistance(a, assist));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobAssistanceSlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(jobAssistanceSlice.name);
const fetchActiveAssistance = network => network.get('/api/v1/activeJobAssistance', jobAssistanceSlice.actions.set);
const fetchJobAssistance = jobid => network => network.get('/api/v1/jobAssistance/' + encodeURIComponent(jobid), jobAssistanceSlice.actions.set);
export const requestAssistance = (clientConfig, network, assistance, jobid) => {
    const toAdd = ensureArray(assistance).map(a => ({ ...a, internalid: crypto.randomUUID(), jobid }));
    return network.post('/api/v1/jobAssistance', toAdd, jobAssistanceSlice.actions.addOrUpdate, { eagerMeta: toAdd.map(a => eagerMeta(a.internalid, { fk: jobid, delta: a }).eagerMeta) })
}
export const removeAssistance = (clientConfig, network, assistance) => {
    return network.delete(`/api/v1/jobAssistance/${encodeURIComponent(assistance.internalid)}`, jobAssistanceSlice.actions.remove, eagerMeta(assistance.internalid, { fk: assistance.jobid }))
}
// #endregion

// #region selectors
const selectAssistance = state => state.jobAssistance.data;
const eagerSelector = createEagerSelector(jobAssistanceSlice.name, { mode: 'mapToArray', key: assistance => assistance.internalid })

const EMPTY_ARRAY = [];
// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectAssistanceByJobID = eagerSelector(createSelector([selectAssistance, (assistance, jobid) => jobid], (assistance, jobid) => {
    return assistance[jobid?.toString()] ?? EMPTY_ARRAY
}));
const selectJobAssistance = jobid => state => selectAssistanceByJobID(state, jobid);
const selectAllAssistance = eagerSelector(createSelector([selectAssistance], assistance => Object.values(assistance).flat()));
// #endregion

// #region listeners
const listen = {
    jobAssistanceRequested: (assistance, { dispatch }) => dispatch(jobAssistanceSlice.actions.addOrUpdate(assistance)),
    jobAssistanceRemoved: (assistance, { dispatch }) => dispatch(jobAssistanceSlice.actions.remove(assistance))
}
// #endregion

export const [useJobAssistance, realizeJobAssistance] = dataHookFactory(selectJobAssistance, initMapFactory(fetchJobAssistance, listen, jobid => jobid?.toString()));
export const [useAllJobAssistance, realizeAllJobAssistance] = dataHookFactory(selectAllAssistance, { run: fetchActiveAssistance, listen });
export const [useAllJobAssistanceMap] = dataHookFactory(selectAssistance, { run: fetchActiveAssistance, listen });

const reducerDetails = { name: jobAssistanceSlice.name, reducer: jobAssistanceSlice.reducer };
export default reducerDetails;